import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { FaEdit, FaTrash, FaPlus, FaFileExcel, FaSearch } from "react-icons/fa";
import AddEditRestaurantMaster from "./AddEditRestaurantMaster";
import axiosInstance from "../../Components/api/axiosInstance";

const RestaurantMaster = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);

  /* ================= FETCH GROUPS ================= */
  const fetchGroups = async () => {
    try {
      const res = await axiosInstance.get("/GroupMaster");
      const options = (res.data || []).map((g) => ({
        value: g.groupId,
        label: g.groupName,
      }));
      setGroups(options);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= FETCH RESTAURANTS ================= */
  const fetchRestaurants = async (groupId) => {
    if (!groupId) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/RestaurantMaster/GetByGroup?groupId=${groupId}`
      );
      setData(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) fetchRestaurants(selectedGroup.value);
  }, [selectedGroup]);

  /* ================= SEARCH ================= */
  const filteredData = useMemo(() => {
    return data.filter((item) =>
      item.restaurantName?.toLowerCase().includes(search.toLowerCase())
    );
  }, [data, search]);

  /* ================= ADD / EDIT ================= */
  const openAdd = () => {
    if (!selectedGroup) {
      alert("Select group first");
      return;
    }
    setEditingData(null);
    setFormOpen(true);
  };
  const openEdit = (row) => {
    setEditingData(row);
    setFormOpen(true);
  };

  /* ================= DELETE ================= */
  const handleDelete = async (row) => {
    if (!window.confirm("Delete this restaurant?")) return;
    try {
      await axiosInstance.post("/RestaurantMaster/UpsertRestaurant", {
        ...row,
        status: false,
      });
      fetchRestaurants(selectedGroup.value);
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= EXPORT ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;
    const rows = filteredData.map((r) => ({
      RestaurantId: r.restId,
      RestaurantName: r.restaurantName,
      BrandName: r.brandName,
      ContactPerson: r.contactPersonName,
      Mobile: r.contactPersonMobile,
      Email: r.contactPersonEmail,
      BiometricId: r.biometricId,
      MaxEmployees: r.maxEmployees,
      Status: r.status ? "Active" : "Inactive",
    }));

    const csv =
      "\uFEFF" +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) =>
          Object.values(e).map((v) => `"${v ?? ""}"`).join(",")
        ),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "RestaurantMaster.csv";
    link.click();
  };

  /* ================= UI ================= */
  return (
    <div className="bg-white rounded-xl shadow-lg p-5 flex flex-col h-[calc(100vh-120px)]">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-3 mb-4">

        <h2 className="text-xl font-bold">Restaurant Master</h2>

        <div className="flex flex-col md:flex-row gap-2 md:items-center flex-wrap">

          {/* GROUP SELECT */}
          <div className="w-full md:w-64">
            <Select
              options={groups}
              value={selectedGroup}
              onChange={setSelectedGroup}
              placeholder="Select Group"
            />
          </div>

          {/* SEARCH */}
          <div className="relative w-full md:w-auto">
            <FaSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search restaurant..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full md:w-56 pl-9 pr-3 py-2 border rounded-lg"
            />
          </div>

          {/* BUTTONS */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={exportExcel}
              className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg"
            >
              <FaFileExcel /> Excel
            </button>
            <button
              onClick={openAdd}
              className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg"
            >
              <FaPlus /> Add
            </button>
          </div>

        </div>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block flex-1 overflow-auto border rounded-lg">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="border p-2">Rest Id</th>
                <th className="border p-2">Restaurant Name</th>
                <th className="border p-2">Brand</th>
                <th className="border p-2">Contact</th>
                <th className="border p-2">Mobile</th>
                <th className="border p-2">Email</th>
                <th className="border p-2">Biometric</th>
                <th className="border p-2">Employees</th>
                <th className="border p-2">Status</th>
                <th className="border p-2 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center p-5">
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center p-5">
                    No Data Found
                  </td>
                </tr>
              ) : (
                filteredData.map((row) => (
                  <tr key={row.restId} className="hover:bg-slate-50">
                    <td className="border p-2">{row.restId}</td>
                    <td className="border p-2">{row.restaurantName}</td>
                    <td className="border p-2">{row.brandName}</td>
                    <td className="border p-2">{row.contactPersonName}</td>
                    <td className="border p-2">{row.contactPersonMobile}</td>
                    <td className="border p-2">{row.contactPersonEmail}</td>
                    <td className="border p-2">{row.biometricId}</td>
                    <td className="border p-2">{row.maxEmployees}</td>
                    <td className="border p-2">{row.status ? "Active" : "Inactive"}</td>
                    <td className="border p-2 text-center">
                      <div className="flex justify-center gap-2">
                        <FaEdit
                          onClick={() => openEdit(row)}
                          className="cursor-pointer text-blue-600"
                        />
                        <FaTrash
                          onClick={() => handleDelete(row)}
                          className="cursor-pointer text-red-600"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOBILE CARD VIEW */}
      <div className="md:hidden flex-1 overflow-auto space-y-3">
        {loading ? (
          <div className="text-center py-6">Loading...</div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-6">No Data Found</div>
        ) : (
          filteredData.map((row) => (
            <div key={row.restId} className="border rounded-xl p-4 shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold text-slate-800">{row.restaurantName}</h3>
                <span
                  className={`text-xs px-3 py-1.5 rounded-full ${
                    row.status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}
                >
                  {row.status ? "Active" : "Inactive"}
                </span>
              </div>
              <p className="text-sm text-slate-600 mb-1"><span className="font-medium">ID:</span> {row.restId}</p>
              <p className="text-sm text-slate-600 mb-1"><span className="font-medium">Brand:</span> {row.brandName || "-"}</p>
              <p className="text-sm text-slate-600 mb-1"><span className="font-medium">Contact:</span> {row.contactPersonName || "-"}</p>
              <p className="text-sm text-slate-600 mb-1"><span className="font-medium">Mobile:</span> {row.contactPersonMobile || "-"}</p>
              <p className="text-sm text-slate-600 mb-1"><span className="font-medium">Email:</span> {row.contactPersonEmail || "-"}</p>
              <p className="text-sm text-slate-600 mb-3"><span className="font-medium">Employees:</span> {row.maxEmployees || "-"}</p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => openEdit(row)}
                  className="bg-blue-50 p-2 rounded-md text-blue-600 hover:bg-blue-100"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDelete(row)}
                  className="bg-red-50 p-2 rounded-md text-red-600 hover:bg-red-100"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* FORM MODAL */}
     {formOpen && (
  <AddEditRestaurantMaster
    editingData={editingData}
    groupId={selectedGroup?.value}
    groupName={selectedGroup?.label}
    onClose={() => setFormOpen(false)}
    onSuccess={() => fetchRestaurants(selectedGroup?.value)}
  />
)}

    </div>
  );
};

export default RestaurantMaster;